import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ActivityIndicator,
  Pressable,
  InteractionManager,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Ionicons from '@expo/vector-icons/Ionicons';
import { auth } from '../../config/firebase';
import { 
  Text, 
  ChatAvatar,
  InstallAppPrompt,
} from '../../components/ui';
import { Theme } from '../../utils/theme';
import { useMentorChat, ChatMessage } from '../../contexts/MentorChatContext';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import ChatInput from './components/ChatInput';
import MessageBubble from './components/MessageBubble';
import QuickActionPills, { QuickAction } from './components/QuickActionPills';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { useSubscription } from '../../hooks/useSubscription';

// Constants for audio player
const MINIMAL_PLAYER_HEIGHT = 64; // Height of the minimal audio player

const MentorChatScreen: React.FC = () => {
  useNavigationLogging('MentorChatScreen');
  
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const flatListRef = useRef<FlatList>(null);
  const { 
    messages, 
    isLoading, 
    isStreaming, 
    sendMessage, 
    loadChatHistory,
    handleQuickAction,
    currentStreamingMessage,
    profile,
    error,
    lastFailedMessage,
    retryLastMessage,
    clearError,
    markChatAsRead,
  } = useMentorChat();
  const { isMinimalPlayerVisible, isFullPlayerVisible } = useAudioPlayer();
  const { hasAIAccess } = useSubscription();

  const [userInitials, setUserInitials] = useState<string>('');
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState<boolean>(false);
  const [isInitialScrollComplete, setIsInitialScrollComplete] = useState<boolean>(false);
  const [hasAttemptedToSend, setHasAttemptedToSend] = useState<boolean>(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState<boolean>(false);

  // Constants for free trial
  const FREE_MESSAGE_LIMIT = 5;

  // Count user messages to determine if user is still in free trial
  const userMessageCount = messages.filter(msg => msg.role === 'user').length;
  const hasFreeTrial = userMessageCount < FREE_MESSAGE_LIMIT;
  const canSendMessage = hasAIAccess || hasFreeTrial;

  // Smooth scroll to absolute bottom
  const scrollToBottom = (animated: boolean = true) => {
    if (flatListRef.current) {
      // Use a very large offset to ensure we reach the absolute bottom
      flatListRef.current.scrollToOffset({ 
        offset: 999999, 
        animated 
      });
    }
  };


  useEffect(() => {
    loadChatHistory();
    
    // Get user initials
    const user = auth.currentUser;
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      const initials = names.map((name: string) => name.charAt(0)).join('').substring(0, 2);
      setUserInitials(initials);
    } else if (user?.email) {
      setUserInitials(user.email.charAt(0));
    }
  }, [loadChatHistory]);

  // Initial scroll to bottom when messages first load
  useEffect(() => {
    if (!isLoading && messages.length > 0 && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
      // Use InteractionManager to ensure scroll happens after all layout is complete
      InteractionManager.runAfterInteractions(() => {
        setTimeout(() => {
          scrollToBottom();
          // Mark initial scroll as complete after a delay
          setTimeout(() => {
            setIsInitialScrollComplete(true);
          }, 500);
        }, 100);
      });
    }
  }, [messages, isLoading, hasInitiallyLoaded]);

  // Auto-scroll to bottom on first entry, preserve position on return
  useEffect(() => {
    const unsubscribeFocus = navigation.addListener('focus', () => {
      // Mark chat as read when screen is focused
      markChatAsRead();

      // Always scroll to bottom on first entry to the screen
      if (messages.length > 0 && !isInitialScrollComplete) {
        setTimeout(() => scrollToBottom(false), 100);
      }
    });

    const unsubscribeBlur = navigation.addListener('blur', () => {
      // Also mark as read when leaving to ensure any new messages during session are covered
      markChatAsRead();
    });

    return () => {
      unsubscribeFocus();
      unsubscribeBlur();
    };
  }, [navigation, messages.length, isAtBottom, hasInitiallyLoaded, markChatAsRead]);

  // Auto-scroll to bottom on initial load and new messages when at bottom
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      if (isAtBottom) {
        // Small delay to ensure content is rendered, use animated scroll
        setTimeout(() => scrollToBottom(true), 100);
      }
    }
  }, [messages, isLoading, isAtBottom]);

  const handleSendMessage = async (message: string) => {
    if (!canSendMessage) {
      setHasAttemptedToSend(true);
      return;
    }
    await sendMessage(message);
    // Always scroll to bottom after user sends a message to show their bubble
    // Use InteractionManager to ensure scroll happens after React finishes rendering
    InteractionManager.runAfterInteractions(() => {
      setTimeout(() => scrollToBottom(true), 100);
    });
  };

  const handleQuickActionPress = (action: QuickAction) => {
    if (!canSendMessage) {
      setHasAttemptedToSend(true);
      return;
    }
    handleQuickAction(action.userMessage, action.mentorResponse);
    // Scroll to bottom after quick action to show the new messages
    // Use InteractionManager to ensure scroll happens after React finishes rendering
    InteractionManager.runAfterInteractions(() => {
      setTimeout(() => scrollToBottom(true), 100);
    });
  };

  const handleNavigateToItem = React.useCallback(async (type: 'book' | 'insight', id: string, providedBookId?: string) => {
    
    if (type === 'book') {
      // Simply navigate with minimal parameters - let BookDetailScreen fetch its data
      (navigation as any).navigate('BookDetail', { 
        bookId: id, 
        languageCode: 'en',
        fromRoute: 'MentorChat',
      });
    } else if (type === 'insight') {
      // SIMPLIFIED: Just pass the insight IDs and let InsightDetailScreen fetch the data
      
      // Find the SPECIFIC message containing this insight (not all messages)
      let insightIds: string[] = [];
      let currentIndex = -1;
      let bookIdFromRecommendation: string | undefined;
      
      // Search through messages in reverse order (newest first) to find the specific bubble
      for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.recommendations?.insights) {
          const foundIndex = message.recommendations.insights.findIndex(
            insight => insight.insightId.toString() === id || 
                       insight.insightId.toString().split('_')[0] === id
          );
          
          if (foundIndex !== -1) {
            // Only get insights from THIS specific message/bubble
            insightIds = message.recommendations.insights.map(i => i.insightId.toString());
            currentIndex = foundIndex;
            // Get bookId from the recommendation if available
            bookIdFromRecommendation = message.recommendations.insights[foundIndex].bookId;
            break; // Stop after finding the first (most recent) match
          }
        }
      }
      
      if (insightIds.length === 0 || currentIndex === -1) {
        console.error('❌ Could not find insight in any message:', id);
        alert('Unable to find this insight in the conversation.');
        return;
      }
      
      // Pass the IDs - DO NOT pass bookId as it triggers full book loading
      // The insightIds path will load book details per insight automatically
      (navigation as any).navigate('InsightDetail', {
        insightIds, // Just pass IDs, not full objects
        currentIndex,
        isMixedInsights: true,
        fromRoute: 'MentorChat',
        // Don't pass bookId - it causes full book loading with all insights + intro
      });
    }
  }, [messages, navigation]); // Memoize with dependencies



  const renderMessage = ({ item, index }: { item: ChatMessage; index: number }) => {
    // Check if next message is also from the same sender
    const nextMessage = messages[index + 1];
    const isLastInSequence = !nextMessage || nextMessage.role !== item.role;
    
    return (
      <MessageBubble
        message={item}
        userInitials={userInitials}
        onNavigateToItem={handleNavigateToItem}
        showAvatar={isLastInSequence}
      />
    );
  };

  const handleScroll = (event: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const threshold = 50; // pixels from bottom to still consider "at bottom"
    const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    const nowAtBottom = distanceFromBottom <= threshold;
    setIsAtBottom(nowAtBottom);
    setShowScrollToBottom(!nowAtBottom);
  };

  const renderHeader = () => {
    return (
      <View style={styles.header}>
        {/* Back Button - Left */}
        <Pressable 
          style={styles.headerButton}
          onPress={() => {
            // Go back to previous screen (typically Home/Dashboard)
            navigation.goBack();
          }}
        >
          <Ionicons name="arrow-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>

        {/* Center Content */}
        <View style={styles.headerCenter}>
          <ChatAvatar type="mentor" size="medium" />
          <View style={styles.headerText}>
            <Text variant="heading3" style={styles.mentorName}>
              Your Mentor
            </Text>
            <Text variant="caption" color="secondary" style={styles.mentorStatus}>
              {isStreaming ? 'typing...' : 'Ready to help'}
            </Text>
          </View>
        </View>

        {/* Right spacer to balance layout */}
        <View style={styles.headerSpacer} />
      </View>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyStateContainer}>
      <View style={styles.emptyStateContent}>
        <ChatAvatar type="mentor" size="large" />
        <Text variant="heading3" align="center" style={styles.emptyTitle}>
          Hello! I'm your mentor
        </Text>
        <Text variant="body2" color="secondary" align="center" style={styles.emptyDescription}>
          I'm here to help with your personal development journey. Ask me about:
          {'\n\n'}• Book recommendations for your goals
          {'\n'}• Guidance on building better habits  
          {'\n'}• Strategies for overcoming challenges
          {'\n'}• Insights for personal growth
          {'\n\n'}What would you like to work on today?
        </Text>
      </View>
    </View>
  );

  const renderFreeTrialBanner = () => {
    const messagesRemaining = FREE_MESSAGE_LIMIT - userMessageCount;
    
    if (hasAIAccess) return null; // Don't show if user has subscription
    
    // Show encouraging banner when approaching limit (last 2 messages)
    if (messagesRemaining > 0 && messagesRemaining <= 2) {
      return (
        <View style={styles.freeTrialWarningBanner}>
          <View style={styles.freeTrialContent}>
            <Ionicons name="star" size={20} color={Theme.colors.primary} />
            <Text variant="body2" style={styles.freeTrialText}>
              {messagesRemaining === 1 
                ? "You have 1 free message remaining" 
                : `You have ${messagesRemaining} free messages remaining`}
            </Text>
          </View>
        </View>
      );
    }
    
    // Show upgrade banner when limit reached
    // Only show AFTER user tries to send (so input stays active until they attempt)
    if (messagesRemaining <= 0 && hasAttemptedToSend) {
      return (
        <View style={styles.freeTrialUpgradeBanner}>
          <View style={styles.freeTrialUpgradeContent}>
            <Ionicons name="sparkles" size={24} color={Theme.colors.success} />
            <View style={styles.freeTrialUpgradeText}>
              <Text variant="heading3" style={styles.freeTrialUpgradeTitle}>
                {Platform.OS === 'web' ? 'Get the Full App! ✨' : 'Enjoyed your free trial? ✨'}
              </Text>
              <Text variant="body2" color="secondary" style={styles.freeTrialUpgradeDescription}>
                {Platform.OS === 'web'
                  ? `You've used all ${FREE_MESSAGE_LIMIT} free messages. Install the mobile app to continue your growth journey with unlimited AI mentor access!`
                  : `You've used all ${FREE_MESSAGE_LIMIT} free messages. Upgrade to continue your growth journey with unlimited AI mentor access!`
                }
              </Text>
            </View>
          </View>
          {Platform.OS === 'web' ? (
            <Pressable
              style={styles.upgradeButton}
              onPress={() => setShowInstallPrompt(true)}
            >
              <Text variant="body1" style={styles.upgradeButtonText}>
                Get the Full App
              </Text>
              <Ionicons name="arrow-forward" size={20} color={Theme.colors.surface} />
            </Pressable>
          ) : (
            <Pressable
              style={styles.upgradeButton}
              onPress={() => navigation.navigate('Paywall' as never)}
            >
              <Text variant="body1" style={styles.upgradeButtonText}>
                Upgrade to AI Platinum
              </Text>
              <Ionicons name="arrow-forward" size={20} color={Theme.colors.surface} />
            </Pressable>
          )}
        </View>
      );
    }
    
    return null;
  };

  const renderErrorBanner = () => {
    if (!error) return null;

    return (
      <View style={styles.errorBanner}>
        <View style={styles.errorContent}>
          <Ionicons name="alert-circle" size={20} color={Theme.colors.error} />
          <Text variant="body2" style={styles.errorText}>
            {error}
          </Text>
        </View>
        <View style={styles.errorActions}>
          <Pressable
            style={[styles.errorButton, styles.retryButton]}
            onPress={retryLastMessage}
          >
            <Text variant="body2" style={styles.retryButtonText}>
              Retry
            </Text>
          </Pressable>
          <Pressable
            style={[styles.errorButton, styles.dismissButton]}
            onPress={clearError}
          >
            <Text variant="body2" style={styles.dismissButtonText}>
              Dismiss
            </Text>
          </Pressable>
        </View>
      </View>
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        {/* Sticky Header */}
        {renderHeader()}
        
        {/* Loading Content */}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
          <Text variant="body2" color="secondary" style={styles.loadingText}>
            Loading your conversation...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const bottomContent = (
    <View style={styles.bottomContainer}>
      {renderErrorBanner()}
      {renderFreeTrialBanner()}
      
      <QuickActionPills
        onQuickAction={handleQuickActionPress}
        disabled={isStreaming}
      />
      
      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isStreaming}
        isStreaming={isStreaming}
        style={
          isMinimalPlayerVisible && !isFullPlayerVisible
            ? { paddingBottom: MINIMAL_PLAYER_HEIGHT / 2 }
            : undefined
        }
      />
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Sticky Header - Outside KeyboardAvoidingView */}
      {renderHeader()}
      
      {/* Main Content Area */}
      <View style={styles.contentWrapper}>
        {/* Scrollable Messages - Takes available space */}
        <View style={styles.messagesContainer}>
          <FlatList
            ref={flatListRef}
            data={messages}
            renderItem={renderMessage}
            keyExtractor={(item) => item.id}
            style={styles.messagesList}
            contentContainerStyle={[
              styles.messagesContent,
              messages.length === 0 && styles.emptyMessagesContent,
            ]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            ListEmptyComponent={renderEmptyState}
            onLayout={() => {
              // Ensure we scroll to bottom after initial layout - only during initial load
              if (!isLoading && messages.length > 0 && hasInitiallyLoaded && !isInitialScrollComplete) {
                setTimeout(() => scrollToBottom(false), 50);
              }
            }}
            onContentSizeChange={() => {
              // This is crucial for first-time load - scroll when content size is known
              // But ONLY during initial load, not during manual scrolling
              if (!isLoading && messages.length > 0 && hasInitiallyLoaded && !isInitialScrollComplete) {
                setTimeout(() => scrollToBottom(false), 100);
              } else if (isInitialScrollComplete && isAtBottom) {
                // Smooth animated scroll for new messages if user is at bottom
                setTimeout(() => scrollToBottom(true), 50);
              }
            }}
          />
          {showScrollToBottom && (
            <Pressable
              style={[
                styles.scrollToBottomButton,
                isMinimalPlayerVisible && !isFullPlayerVisible ? { bottom: 96 + MINIMAL_PLAYER_HEIGHT } : undefined,
              ]}
              onPress={() => scrollToBottom(true)}
              accessibilityLabel="Scroll to latest messages"
            >
              <Ionicons name="arrow-down" size={20} color={Theme.colors.surface} />
            </Pressable>
          )}
        </View>
        
        {/* Sticky Bottom Input */}
        {Platform.OS === 'ios' ? (
          <KeyboardAvoidingView 
            behavior="padding"
            keyboardVerticalOffset={insets.top + 60}
          >
            {bottomContent}
          </KeyboardAvoidingView>
        ) : (
          bottomContent
        )}
      </View>
      {Platform.OS === 'web' && (
        <InstallAppPrompt
          visible={showInstallPrompt}
          onDismiss={() => setShowInstallPrompt(false)}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.colors.background,
  },
  contentWrapper: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.md,
    backgroundColor: Theme.colors.surface,
    borderBottomWidth: 0.5,
    borderBottomColor: Theme.colors.border,
  },
  headerButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerCenter: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
    marginLeft: Theme.spacing.xs,
  },
  headerText: {
    flex: 1,
  },
  mentorNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mentorName: {
    marginBottom: 0,
    fontSize: 16,
    fontWeight: '400',
  },
  mentorStatus: {
    fontSize: 11,
    marginTop: -6,
  },
  headerSpacer: {
    width: 40,
  },
  messagesContainer: {
    flex: 1,
  },
  messagesList: {
    flex: 1,
  },
  messagesContent: {
    paddingVertical: Theme.spacing.md,
  },
  emptyMessagesContent: {
    flexGrow: 1,
  },
  bottomContainer: {
    backgroundColor: Theme.colors.surface,
  },
  scrollToBottomButton: {
    position: 'absolute',
    right: Theme.spacing.lg,
    bottom: 96,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Theme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...Theme.shadows.md,
  },
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.xxxl,
  },
  emptyStateContent: {
    alignItems: 'center',
    gap: Theme.spacing.lg,
  },
  emptyTitle: {
    marginTop: Theme.spacing.md,
  },
  emptyDescription: {
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: Theme.spacing.md,
  },
  loadingText: {
    textAlign: 'center',
  },
  errorBanner: {
    backgroundColor: '#FEE2E2',
    borderRadius: Theme.borderRadius.md,
    padding: Theme.spacing.md,
    marginHorizontal: Theme.spacing.md,
    marginTop: Theme.spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: Theme.colors.error,
    gap: Theme.spacing.sm,
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.sm,
  },
  errorText: {
    flex: 1,
    color: Theme.colors.error,
    lineHeight: 20,
  },
  errorActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: Theme.spacing.sm,
    marginTop: Theme.spacing.xs,
  },
  errorButton: {
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.sm,
    minWidth: 80,
    alignItems: 'center',
  },
  retryButton: {
    backgroundColor: Theme.colors.error,
  },
  retryButtonText: {
    color: Theme.colors.surface,
    fontWeight: '600',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: Theme.colors.error,
  },
  dismissButtonText: {
    color: Theme.colors.error,
    fontWeight: '600',
  },
  freeTrialWarningBanner: {
    backgroundColor: Theme.colors.primary + '15',
    borderRadius: Theme.borderRadius.md,
    padding: Theme.spacing.md,
    marginHorizontal: Theme.spacing.md,
    marginTop: Theme.spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: Theme.colors.primary,
  },
  freeTrialContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
  },
  freeTrialText: {
    flex: 1,
    color: Theme.colors.primary,
    fontWeight: '600',
    lineHeight: 20,
  },
  freeTrialUpgradeBanner: {
    backgroundColor: Theme.colors.success + '15',
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.lg,
    marginHorizontal: Theme.spacing.md,
    marginTop: Theme.spacing.md,
    borderWidth: 2,
    borderColor: Theme.colors.success + '40',
    gap: Theme.spacing.md,
  },
  freeTrialUpgradeContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
  },
  freeTrialUpgradeText: {
    flex: 1,
    gap: Theme.spacing.xs,
  },
  freeTrialUpgradeTitle: {
    color: Theme.colors.success,
    fontSize: 18,
  },
  freeTrialUpgradeDescription: {
    lineHeight: 20,
  },
  upgradeButton: {
    backgroundColor: Theme.colors.success,
    paddingVertical: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Theme.spacing.sm,
    ...Theme.shadows.sm,
  },
  upgradeButtonText: {
    color: Theme.colors.surface,
    fontWeight: '700',
    fontSize: 16,
  },
});

export default MentorChatScreen; 